'use client';
/* eslint-disable max-lines-per-function */

/**
 * Family / Guardian Access — Switch Family dialog (Unit U4).
 *
 * REQUIREMENTS SUMMARY (U4 — Active family context + Switch Family):
 * - R11/R12 The accessible household picker a multi-family parent uses to change
 *   the active dashboard context. Each option shows the disambiguating label
 *   (family name + first athlete names) so two same-named households are
 *   distinguishable.
 * - ACCESSIBILITY (U4 verification): role="dialog" + aria-modal, an accessible
 *   name via aria-labelledby, a focus trap (Tab/Shift+Tab cycle inside),
 *   Escape closes, focus moves to the dialog on open and returns to the trigger
 *   on close, and every option is a real radio with a 44px minimum touch target.
 * - RESPONSIVE: full-screen sheet on narrow widths, centered card on desktop.
 * - The dialog is purely presentational: it receives the families + active id and
 *   reports a chosen id. It never fetches and never trusts a client id on its own
 *   — the parent (SwitchFamilyButton) routes the choice through the server context
 *   via switchFamily().
 *
 * DRY: uses the shared FamilyContextOption shape from the family context; no new
 * modal primitive — a small self-contained focus-trapped dialog keeps the a11y
 * contract explicit and unit-testable.
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { FamilyContextOption } from '@/context/FamilyContext';

export interface SwitchFamilyDialogProps {
  isOpen: boolean;
  families: FamilyContextOption[];
  activeFamilyAccountId: string | null;
  onSelect: (familyAccountId: string) => void;
  onClose: () => void;
  /** Disables options while a switch is in flight. */
  busy?: boolean;
}

const TITLE_ID = 'switch-family-dialog-title';
const DESC_ID = 'switch-family-dialog-desc';

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter((el) => el.offsetParent !== null || el === document.activeElement);
}

export function SwitchFamilyDialog({
  isOpen,
  families,
  activeFamilyAccountId,
  onSelect,
  onClose,
  busy = false,
}: SwitchFamilyDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const [mounted, setMounted] = React.useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Move focus into the dialog on open; restore it to the trigger on close.
  useEffect(() => {
    if (!isOpen) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    // Focus the dialog container so screen readers announce it, then the first option.
    const node = dialogRef.current;
    if (node) {
      const focusables = getFocusable(node);
      (focusables[0] ?? node).focus();
    }
    return () => {
      // Return focus to whatever opened the dialog.
      previouslyFocused.current?.focus?.();
    };
  }, [isOpen]);

  // Lock body scroll while open (full-screen sheet on mobile).
  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [isOpen]);

  // Document-level Escape fallback so closing works regardless of where focus
  // currently sits (e.g. after the backdrop or a radio takes focus). The focus
  // trap below still handles Tab cycling inside the dialog.
  useEffect(() => {
    if (!isOpen) return;
    const onDocKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onDocKeyDown);
    return () => document.removeEventListener('keydown', onDocKeyDown);
  }, [isOpen, onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const node = dialogRef.current;
      if (!node) return;
      const focusables = getFocusable(node);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      // Focus trap: wrap from last→first (Tab) and first→last (Shift+Tab).
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  if (!isOpen || !mounted) return null;

  const dialog = (
    <div
      // `dropdown-content` makes the shared Dropdown's outside-press handler
      // skip this element (it tests `target.closest('.dropdown-content')`).
      // Needed because this dialog is portalled to <body>: for a host that
      // renders it INSIDE an open Dropdown, a press in the dialog would read as
      // a press outside the menu, the menu would close, this dialog would
      // unmount with it, and — mousedown preceding mouseup — the option's click
      // would never run. A host that closes its menu BEFORE opening this dialog
      // never had the problem: its dropdownRef is already null, so the handler
      // short-circuits (the listener itself is always registered).
      //
      // Caveat worth knowing before copying this: the handler's check is global,
      // so a `fixed inset-0` element carrying this class suppresses outside-press
      // dismissal for EVERY mounted Dropdown while it is open. Fine here — this
      // dialog is modal and covers the viewport regardless.
      className="dropdown-content fixed inset-0 z-999999 flex items-end sm:items-center justify-center"
      data-testid="switch-family-overlay"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        aria-hidden="true"
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        aria-describedby={DESC_ID}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className="relative w-full sm:max-w-md bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] sm:max-h-[80vh] overflow-y-auto focus:outline-none"
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-gray-100 dark:border-gray-800">
          <div>
            <h2
              id={TITLE_ID}
              className="text-lg font-semibold text-gray-900 dark:text-white"
            >
              Switch family
            </h2>
            <p id={DESC_ID} className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              Choose which family you want to view. You can switch back anytime.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] -mr-2 -mt-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div role="radiogroup" aria-labelledby={TITLE_ID} className="p-3 space-y-2">
          {families.map((family) => {
            const isActive = family.familyAccountId === activeFamilyAccountId;
            return (
              <button
                key={family.familyAccountId}
                type="button"
                role="radio"
                aria-checked={isActive}
                disabled={busy}
                onClick={() => onSelect(family.familyAccountId)}
                className={`w-full text-left rounded-xl border px-4 py-3 min-h-[44px] transition flex items-center justify-between gap-3 ${
                  isActive
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 ring-1 ring-brand-500'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                } ${busy ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-900 dark:text-white truncate">
                    {family.displayName}
                  </span>
                  {family.athleteNames.length > 0 && (
                    <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                      {family.athleteNames.slice(0, 3).join(', ')}
                      {family.athleteNames.length > 3
                        ? ` +${family.athleteNames.length - 3} more`
                        : ''}
                    </span>
                  )}
                  {family.isPrimaryGuardian && (
                    <span className="mt-1 inline-flex items-center rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 px-2 py-0.5 text-[10px] font-medium">
                      Main guardian
                    </span>
                  )}
                </span>
                {isActive && (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                    className="shrink-0 text-brand-500"
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
}

export default SwitchFamilyDialog;
